import { motion } from "framer-motion";
import { ScanLine, BatteryCharging, PlugZap, Wrench, Package } from "lucide-react";
import { IMAGES } from "../data/site";

const fade = (i = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.55, delay: i * 0.07 },
});

const CardShell = ({ tid, className = "", children }) => (
  <motion.article
    data-testid={tid}
    {...fade()}
    className={`group relative rounded-2xl border border-line bg-surface overflow-hidden h-full transition-all duration-300 hover:border-orange-600/50 hover:-translate-y-1.5 hover:shadow-[0_20px_60px_rgba(234,88,12,0.12)] ${className}`}
  >
    {children}
  </motion.article>
);

const IconChip = ({ icon: Icon }) => (
  <span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-orange-600/10 border border-orange-600/25 text-orange-500">
    <Icon size={20} />
  </span>
);

export const Services = () => (
  <section id="services" data-testid="services-section" className="py-20 sm:py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
      <motion.p
        {...fade()}
        className="font-mono text-[11px] uppercase tracking-[0.28em] text-orange-500 font-semibold mb-4"
      >
        What we do — auto electrical
      </motion.p>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 lg:mb-16">
        <motion.h2
          {...fade(1)}
          data-testid="services-heading"
          className="font-display font-bold uppercase tracking-tight text-slate-50 text-3xl sm:text-4xl lg:text-5xl leading-[0.95] max-w-2xl"
        >
          The general stuff, done properly.
        </motion.h2>
        <motion.p {...fade(2)} className="text-slate-400 max-w-sm text-base leading-relaxed">
          Cars, 4x4s, caravans, trailers and light commercial — serviced where
          they sit, anywhere on the Sunshine Coast.
        </motion.p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
        {/* Dominant diagnostics card */}
        <CardShell tid="bento-card-diagnostics" className="md:col-span-2 lg:col-span-7">
          <div className="grid grid-cols-1 sm:grid-cols-2 h-full">
            <div className="p-7 lg:p-9 flex flex-col justify-center">
              <IconChip icon={ScanLine} />
              <h3 className="font-display font-bold uppercase text-2xl lg:text-3xl text-slate-50 mt-6 tracking-tight">
                Diagnostics &amp; electrical repairs
              </h3>
              <p className="text-slate-400 mt-4 leading-relaxed text-[15px]">
                Warning lights, flat circuits, wiring gremlins — faults found
                with a proper diagnostic scan tool, then repaired. No parts
                roulette, no guesswork.
              </p>
            </div>
            <div className="relative min-h-[220px] sm:min-h-full">
              <img
                src={IMAGES.caravanPanel}
                alt="Neatly wired DC electrical panel in a caravan, fuse box and circuit breakers"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-surface via-transparent to-transparent hidden sm:block" />
              <div className="absolute inset-0 bg-gradient-to-t from-surface/70 to-transparent sm:bg-gradient-to-r sm:from-transparent sm:to-surface/30" />
            </div>
          </div>
        </CardShell>

        {/* Batteries image card */}
        <CardShell tid="bento-card-batteries" className="lg:col-span-5">
          <div className="relative h-52 lg:h-60 overflow-hidden">
            <img
              src={IMAGES.heroBattery}
              alt="New battery with SWS Auto Electrics branding and digital voltage display"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent" />
          </div>
          <div className="p-7 -mt-6 relative">
            <IconChip icon={BatteryCharging} />
            <h3 className="font-display font-bold uppercase text-2xl text-slate-50 mt-5 tracking-tight">
              Batteries, alternators &amp; starters
            </h3>
            <p className="text-slate-400 mt-3 leading-relaxed text-[15px]">
              Batteries, alternators and starter motors supplied and fitted —
              mobile, so you're not calling a tow truck to get it done.
            </p>
          </div>
        </CardShell>

        {/* Towing image card */}
        <CardShell tid="bento-card-towing" className="lg:col-span-4">
          <div className="relative h-52 lg:h-56 overflow-hidden">
            <img
              src={IMAGES.towpro}
              alt="REDARC Tow-Pro brake controller neatly installed in a vehicle dashboard"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent" />
          </div>
          <div className="p-7 -mt-6 relative">
            <IconChip icon={PlugZap} />
            <h3 className="font-display font-bold uppercase text-2xl text-slate-50 mt-5 tracking-tight">
              Towing setups &amp; brake controllers
            </h3>
            <p className="text-slate-400 mt-3 leading-relaxed text-[15px]">
              Caravan towing wiring, Anderson plugs and brake controllers —
              including REDARC Tow-Pro — fitted neatly and tested before you
              hitch up.
            </p>
          </div>
        </CardShell>

        {/* Fit-outs text card */}
        <CardShell tid="bento-card-fitouts" className="lg:col-span-4">
          <div className="p-7 lg:p-8 flex flex-col justify-center h-full">
            <IconChip icon={Wrench} />
            <h3 className="font-display font-bold uppercase text-2xl text-slate-50 mt-5 tracking-tight">
              Vehicle fit-outs
            </h3>
            <p className="text-slate-400 mt-3 leading-relaxed text-[15px]">
              12V accessory wiring, switch panels, lighting, UHF radios and
              custom fit-outs for work rigs and touring rigs alike.
            </p>
          </div>
        </CardShell>

        {/* Parts supply text card */}
        <CardShell tid="bento-card-parts" className="lg:col-span-4">
          <div className="p-7 lg:p-8 flex flex-col justify-center h-full">
            <IconChip icon={Package} />
            <h3 className="font-display font-bold uppercase text-2xl text-slate-50 mt-5 tracking-tight">
              Parts supply
            </h3>
            <p className="text-slate-400 mt-3 leading-relaxed text-[15px]">
              Quality automotive electrical parts supplied and fitted — or
              supplied outright if you're doing the job yourself.
            </p>
          </div>
        </CardShell>
      </div>
    </div>
  </section>
);
